import React, { useCallback, useEffect, useRef, useState } from "react";
import Card from "components/card";
import Navbar from "components/navbar";
import { useTranslation } from "react-i18next";
import Loader from "components/loader/loader";
import { errorToast, successToast } from "utils/commons";
import {
    getHelpArticles,
    createHelpArticle,
    updateHelpArticle,
    deleteHelpArticle,
} from "services/customAPI";
import ReactMarkdown from "react-markdown";
import {
    MdAdd,
    MdEdit,
    MdDelete,
    MdSearch,
    MdArticle,
    MdVisibility,
    MdClose,
    MdToggleOn,
    MdToggleOff,
    MdFilterList,
    MdFormatBold,
    MdFormatItalic,
    MdFormatListBulleted,
    MdFormatListNumbered,
    MdCode,
    MdTitle,
    MdPreview,
    MdEditNote,
} from "react-icons/md";

interface Article {
    id: number;
    title: string;
    content: string;
    category: string;
    audience: string;
    searchKeywords: string;
    sortOrder: number;
    active: boolean;
}

const AUDIENCES = [
    { value: "both", label: "Todos" },
    { value: "rider", label: "Passageiro" },
    { value: "driver", label: "Motorista" },
];

const CATEGORIES = [
    { value: "getting_started", label: "Primeiros Passos" },
    { value: "payments", label: "Pagamentos" },
    { value: "safety", label: "Segurança" },
    { value: "policies", label: "Políticas" },
    { value: "support", label: "Suporte" },
    { value: "faq", label: "FAQ" },
];

const STATUS_FILTERS = [
    { value: "all", label: "Todos" },
    { value: "active", label: "Ativos" },
    { value: "inactive", label: "Inativos" },
];

const AUDIENCE_STYLES: Record<string, { bg: string; text: string }> = {
    rider: { bg: "bg-cyan-500/20", text: "text-cyan-400" },
    driver: { bg: "bg-amber-500/20", text: "text-amber-400" },
    both: { bg: "bg-purple-500/20", text: "text-purple-400" },
};

const CATEGORY_STYLES: Record<string, { bg: string; text: string }> = {
    getting_started: { bg: "bg-green-500/15", text: "text-green-400" },
    payments: { bg: "bg-emerald-500/15", text: "text-emerald-400" },
    safety: { bg: "bg-red-500/15", text: "text-red-400" },
    policies: { bg: "bg-blue-500/15", text: "text-blue-400" },
    support: { bg: "bg-orange-500/15", text: "text-orange-400" },
    faq: { bg: "bg-indigo-500/15", text: "text-indigo-400" },
};

/** Strip markdown to get a plain text preview */
function stripMarkdown(md: string, maxLen = 120): string {
    const plain = md
        .replace(/#{1,6}\s+/g, "")
        .replace(/\*\*(.+?)\*\*/g, "$1")
        .replace(/\*(.+?)\*/g, "$1")
        .replace(/`(.+?)`/g, "$1")
        .replace(/\[(.+?)\]\(.+?\)/g, "$1")
        .replace(/^[-*]\s+/gm, "")
        .replace(/\n+/g, " ")
        .trim();
    return plain.length > maxLen ? `${plain.slice(0, maxLen)}…` : plain;
}

export default function HelpArticlesPage() {
    const { t } = useTranslation();
    const [articles, setArticles] = useState<Article[]>([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState<Article | null>(null);
    const [creating, setCreating] = useState(false);
    const [searchQ, setSearchQ] = useState("");
    const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("active");
    const [previewing, setPreviewing] = useState<Article | null>(null);
    const [editorPreview, setEditorPreview] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const [form, setForm] = useState({
        title: "",
        content: "",
        category: "getting_started",
        audience: "both",
        searchKeywords: "",
        sortOrder: 0,
        active: true,
    });

    const fetchArticles = useCallback(async () => {
        try {
            setLoading(true);
            const params: Record<string, any> = {};
            if (searchQ) params.q = searchQ;
            if (statusFilter === "active") params.active = true;
            else if (statusFilter === "inactive") params.active = false;
            const res = await getHelpArticles(params);
            setArticles(res?.data ?? []);
        } catch {
            errorToast("Erro ao carregar artigos");
        } finally {
            setLoading(false);
        }
    }, [searchQ, statusFilter]);

    useEffect(() => {
        fetchArticles();
    }, [fetchArticles]);

    const openCreate = () => {
        setForm({ title: "", content: "", category: "getting_started", audience: "both", searchKeywords: "", sortOrder: 0, active: true });
        setCreating(true);
        setEditing(null);
        setEditorPreview(false);
    };

    const openEdit = (a: Article) => {
        setForm({ title: a.title, content: a.content, category: a.category, audience: a.audience, searchKeywords: a.searchKeywords || "", sortOrder: a.sortOrder, active: a.active });
        setEditing(a);
        setCreating(false);
        setEditorPreview(false);
    };

    const handleSave = async () => {
        try {
            if (editing) {
                await updateHelpArticle(editing.id, form);
                successToast("Artigo atualizado");
            } else {
                await createHelpArticle(form);
                successToast("Artigo criado");
            }
            setEditing(null);
            setCreating(false);
            fetchArticles();
        } catch {
            errorToast("Erro ao salvar artigo");
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("Excluir este artigo?")) return;
        try {
            await deleteHelpArticle(id);
            successToast("Artigo excluído");
            fetchArticles();
        } catch {
            errorToast("Erro ao excluir");
        }
    };

    const handleToggleActive = async (a: Article) => {
        try {
            await updateHelpArticle(a.id, { active: !a.active });
            successToast(a.active ? "Artigo desativado" : "Artigo ativado");
            fetchArticles();
        } catch {
            errorToast("Erro ao atualizar");
        }
    };

    /** Insert markdown formatting at cursor position */
    const insertMarkdown = (prefix: string, suffix: string = "") => {
        const ta = textareaRef.current;
        if (!ta) return;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const selected = form.content.substring(start, end);
        const replacement = `${prefix}${selected || "texto"}${suffix}`;
        const newContent = form.content.substring(0, start) + replacement + form.content.substring(end);
        setForm({ ...form, content: newContent });
        setTimeout(() => {
            ta.focus();
            ta.selectionStart = start + prefix.length;
            ta.selectionEnd = start + prefix.length + (selected || "texto").length;
        }, 0);
    };

    if (loading && articles.length === 0) {
        return (
            <>
                <Navbar flag={false} brandText="Help Center" />
                <Loader />
            </>
        );
    }

    const showForm = creating || editing;

    // Client-side filter for status (in case backend doesn't support it)
    const filteredArticles = statusFilter === "all"
        ? articles
        : articles.filter(a => statusFilter === "active" ? a.active : !a.active);

    return (
        <>
            <Navbar flag={false} brandText="Help Center — Artigos" />
            <Card extra="w-full mt-4 p-6 h-full">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <MdArticle className="text-cyan-400 text-2xl" />
                        <h2 className="text-xl font-bold text-white">Artigos de Ajuda</h2>
                        <span className="text-sm text-gray-400">({filteredArticles.length})</span>
                    </div>
                    <button
                        onClick={openCreate}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl hover:opacity-90 transition-all text-sm font-semibold"
                    >
                        <MdAdd /> Novo Artigo
                    </button>
                </div>

                {/* Search + Status Filter */}
                <div className="flex gap-3 mb-6">
                    <div className="relative flex-1">
                        <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Buscar artigos..."
                            value={searchQ}
                            onChange={(e) => setSearchQ(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 bg-navy-800 border border-white/10 rounded-xl text-white focus:border-cyan-500 focus:outline-none transition-all"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <MdFilterList className="text-gray-400" />
                        {STATUS_FILTERS.map((sf) => (
                            <button
                                key={sf.value}
                                onClick={() => setStatusFilter(sf.value as any)}
                                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${statusFilter === sf.value
                                    ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/40"
                                    : "bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10"
                                    }`}
                            >
                                {sf.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Form - Editor with Markdown */}
                {showForm && (
                    <Card extra="p-5 mb-6 border border-cyan-500/30 bg-navy-800">
                        <h3 className="text-lg font-bold text-white mb-4">
                            {editing ? "Editar Artigo" : "Novo Artigo"}
                        </h3>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="text-sm text-gray-400 mb-1 block">Título</label>
                                <input
                                    value={form.title}
                                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    className="w-full px-3 py-2 bg-navy-900 border border-white/10 rounded-lg text-white"
                                />
                            </div>
                            <div className="flex gap-4">
                                <div className="flex-1">
                                    <label className="text-sm text-gray-400 mb-1 block">Audiência</label>
                                    <select
                                        value={form.audience}
                                        onChange={(e) => setForm({ ...form, audience: e.target.value })}
                                        className="w-full px-3 py-2 bg-navy-900 border border-white/10 rounded-lg text-white"
                                    >
                                        {AUDIENCES.map((a) => (<option key={a.value} value={a.value}>{a.label}</option>))}
                                    </select>
                                </div>
                                <div className="flex-1">
                                    <label className="text-sm text-gray-400 mb-1 block">Categoria</label>
                                    <select
                                        value={form.category}
                                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                                        className="w-full px-3 py-2 bg-navy-900 border border-white/10 rounded-lg text-white"
                                    >
                                        {CATEGORIES.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Markdown Editor with Toolbar and Preview */}
                        <div className="mb-4">
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm text-gray-400">Conteúdo (Markdown)</label>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => setEditorPreview(false)}
                                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${!editorPreview
                                            ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/40"
                                            : "bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10"
                                            }`}
                                    >
                                        <MdEditNote className="text-sm" /> Editar
                                    </button>
                                    <button
                                        onClick={() => setEditorPreview(true)}
                                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${editorPreview
                                            ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/40"
                                            : "bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10"
                                            }`}
                                    >
                                        <MdPreview className="text-sm" /> Preview
                                    </button>
                                </div>
                            </div>

                            {/* Toolbar */}
                            {!editorPreview && (
                                <div className="flex items-center gap-1 mb-2 p-2 bg-white/5 rounded-lg border border-white/10">
                                    <button
                                        onClick={() => insertMarkdown("**", "**")}
                                        className="p-1.5 hover:bg-white/10 rounded text-gray-400 hover:text-white transition-colors"
                                        title="Negrito"
                                    >
                                        <MdFormatBold className="text-lg" />
                                    </button>
                                    <button
                                        onClick={() => insertMarkdown("*", "*")}
                                        className="p-1.5 hover:bg-white/10 rounded text-gray-400 hover:text-white transition-colors"
                                        title="Itálico"
                                    >
                                        <MdFormatItalic className="text-lg" />
                                    </button>
                                    <div className="w-px h-5 bg-white/10 mx-1" />
                                    <button
                                        onClick={() => insertMarkdown("## ", "")}
                                        className="p-1.5 hover:bg-white/10 rounded text-gray-400 hover:text-white transition-colors"
                                        title="Título"
                                    >
                                        <MdTitle className="text-lg" />
                                    </button>
                                    <button
                                        onClick={() => insertMarkdown("- ", "")}
                                        className="p-1.5 hover:bg-white/10 rounded text-gray-400 hover:text-white transition-colors"
                                        title="Lista"
                                    >
                                        <MdFormatListBulleted className="text-lg" />
                                    </button>
                                    <button
                                        onClick={() => insertMarkdown("1. ", "")}
                                        className="p-1.5 hover:bg-white/10 rounded text-gray-400 hover:text-white transition-colors"
                                        title="Lista Numerada"
                                    >
                                        <MdFormatListNumbered className="text-lg" />
                                    </button>
                                    <button
                                        onClick={() => insertMarkdown("`", "`")}
                                        className="p-1.5 hover:bg-white/10 rounded text-gray-400 hover:text-white transition-colors"
                                        title="Código"
                                    >
                                        <MdCode className="text-lg" />
                                    </button>
                                </div>
                            )}

                            {/* Editor / Preview split */}
                            <div className={editorPreview ? "" : "grid grid-cols-1 lg:grid-cols-2 gap-4"}>
                                {!editorPreview && (
                                    <textarea
                                        ref={textareaRef}
                                        value={form.content}
                                        onChange={(e) => setForm({ ...form, content: e.target.value })}
                                        rows={14}
                                        className="w-full px-3 py-2 bg-navy-900 border border-white/10 rounded-lg text-white font-mono text-sm resize-y"
                                        placeholder="Escreva o conteúdo do artigo em Markdown..."
                                    />
                                )}
                                <div className={`${editorPreview ? 'min-h-[300px]' : ''} px-4 py-3 bg-navy-900 border border-white/10 rounded-lg overflow-y-auto`}
                                    style={editorPreview ? {} : { maxHeight: '360px' }}
                                >
                                    <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-semibold">
                                        Preview
                                    </div>
                                    <div className="prose prose-invert text-white prose-sm max-w-none
                                        prose-headings:text-white prose-headings:font-bold
                                        prose-h1:text-2xl prose-h1:mb-4 prose-h1:mt-0
                                        prose-h2:text-lg prose-h2:mb-3 prose-h2:mt-6
                                        prose-h3:text-base prose-h3:mb-2 prose-h3:mt-4
                                        prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-3
                                        prose-li:text-gray-300 prose-li:mb-1
                                        prose-strong:text-white
                                        prose-ul:pl-5 prose-ol:pl-5
                                        prose-code:text-purple-300 prose-code:bg-purple-500/10 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
                                        prose-hr:border-white/10
                                    ">
                                        {form.content ? (
                                            <ReactMarkdown>{form.content}</ReactMarkdown>
                                        ) : (
                                            <p className="text-gray-500 italic">O preview do conteúdo aparecerá aqui...</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="text-sm text-gray-400 mb-1 block">Palavras-chave (para busca)</label>
                                <input
                                    value={form.searchKeywords}
                                    onChange={(e) => setForm({ ...form, searchKeywords: e.target.value })}
                                    className="w-full px-3 py-2 bg-navy-900 border border-white/10 rounded-lg text-white"
                                    placeholder="palavra1 palavra2 palavra3"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-gray-400 mb-1 block">Ordem</label>
                                <input
                                    type="number"
                                    value={form.sortOrder}
                                    onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
                                    className="w-full px-3 py-2 bg-navy-900 border border-white/10 rounded-lg text-white"
                                />
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <button onClick={handleSave} className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl text-sm font-semibold">
                                Salvar
                            </button>
                            <button onClick={() => { setEditing(null); setCreating(false); }} className="px-6 py-2 bg-white/10 text-gray-300 rounded-xl text-sm">
                                Cancelar
                            </button>
                        </div>
                    </Card>
                )}

                {/* Articles Grid - Card Layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filteredArticles.map((a) => {
                        const audStyle = AUDIENCE_STYLES[a.audience] || AUDIENCE_STYLES.both;
                        const catStyle = CATEGORY_STYLES[a.category] || { bg: "bg-gray-500/15", text: "text-gray-400" };
                        return (
                            <div
                                key={a.id}
                                className={`group relative flex flex-col rounded-2xl border p-5 transition-all duration-300 hover:shadow-lg ${a.active
                                    ? "border-white/10 bg-gradient-to-br from-white/5 to-transparent hover:border-white/20 hover:from-white/8"
                                    : "border-white/5 bg-white/[0.02] opacity-60"
                                    }`}
                            >
                                {/* Status badge */}
                                <span className={`absolute top-3 right-3 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${a.active
                                    ? "bg-green-500/20 text-green-400"
                                    : "bg-red-500/20 text-red-400"
                                    }`}>
                                    {a.active ? "Ativo" : "Inativo"}
                                </span>

                                {/* Title */}
                                <h3 className="text-base font-bold text-white mb-2 pr-16 line-clamp-2">
                                    {a.title}
                                </h3>

                                {/* Badges */}
                                <div className="flex items-center gap-2 mb-3">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${audStyle.bg} ${audStyle.text}`}>
                                        {AUDIENCES.find((au) => au.value === a.audience)?.label}
                                    </span>
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${catStyle.bg} ${catStyle.text}`}>
                                        {CATEGORIES.find((c) => c.value === a.category)?.label || a.category}
                                    </span>
                                    <span className="text-[10px] text-gray-500 ml-auto">
                                        #{a.sortOrder}
                                    </span>
                                </div>

                                {/* Content preview */}
                                <p className="text-xs text-gray-400 leading-relaxed mb-4 flex-1 line-clamp-3">
                                    {stripMarkdown(a.content)}
                                </p>

                                {/* Actions */}
                                <div className="flex items-center gap-1 pt-3 border-t border-white/5">
                                    <button
                                        onClick={() => setPreviewing(a)}
                                        className="flex items-center gap-1 px-2.5 py-1.5 hover:bg-white/10 rounded-lg text-gray-400 hover:text-cyan-400 transition-colors text-xs"
                                        title="Visualizar"
                                    >
                                        <MdVisibility className="text-sm" />
                                        <span className="hidden sm:inline">Ver</span>
                                    </button>
                                    <button
                                        onClick={() => openEdit(a)}
                                        className="flex items-center gap-1 px-2.5 py-1.5 hover:bg-white/10 rounded-lg text-gray-400 hover:text-cyan-400 transition-colors text-xs"
                                    >
                                        <MdEdit className="text-sm" />
                                        <span className="hidden sm:inline">Editar</span>
                                    </button>
                                    <button
                                        onClick={() => handleToggleActive(a)}
                                        className={`flex items-center gap-1 px-2.5 py-1.5 hover:bg-white/10 rounded-lg transition-colors text-xs ${a.active
                                            ? "text-green-400 hover:text-red-400"
                                            : "text-red-400 hover:text-green-400"
                                            }`}
                                        title={a.active ? "Desativar" : "Ativar"}
                                    >
                                        {a.active ? <MdToggleOn className="text-lg" /> : <MdToggleOff className="text-lg" />}
                                    </button>
                                    <button
                                        onClick={() => handleDelete(a.id)}
                                        className="ml-auto flex items-center gap-1 px-2.5 py-1.5 hover:bg-white/10 rounded-lg text-gray-400 hover:text-red-400 transition-colors text-xs"
                                    >
                                        <MdDelete className="text-sm" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {filteredArticles.length === 0 && !loading && (
                    <p className="text-center text-gray-500 py-8">
                        {statusFilter === "inactive"
                            ? "Nenhum artigo inativo encontrado"
                            : "Nenhum artigo encontrado"}
                    </p>
                )}
            </Card>

            {/* Preview Modal */}
            {previewing && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-navy-800 rounded-2xl border border-white/10 w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
                        {/* Modal header */}
                        <div className="flex items-center justify-between p-5 border-b border-white/10">
                            <div className="flex items-center gap-3">
                                <MdArticle className="text-cyan-400 text-xl" />
                                <h3 className="text-lg font-bold text-white">{previewing.title}</h3>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${previewing.active
                                    ? 'bg-green-500/20 text-green-400'
                                    : 'bg-red-500/20 text-red-400'
                                    }`}>
                                    {previewing.active ? 'Ativo' : 'Inativo'}
                                </span>
                            </div>
                            <button
                                onClick={() => setPreviewing(null)}
                                className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors"
                            >
                                <MdClose className="text-xl" />
                            </button>
                        </div>
                        {/* Modal meta */}
                        <div className="flex items-center gap-4 px-5 py-3 border-b border-white/5">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${(AUDIENCE_STYLES[previewing.audience] || AUDIENCE_STYLES.both).bg} ${(AUDIENCE_STYLES[previewing.audience] || AUDIENCE_STYLES.both).text}`}>
                                {AUDIENCES.find((au) => au.value === previewing.audience)?.label}
                            </span>
                            <span className="text-xs text-gray-500">
                                {CATEGORIES.find((c) => c.value === previewing.category)?.label || previewing.category}
                            </span>
                        </div>
                        {/* Modal content */}
                        <div className="overflow-y-auto p-6 flex-1">
                            <div className="prose prose-invert text-white prose-sm max-w-none
                                prose-headings:text-white prose-headings:font-bold
                                prose-h1:text-2xl prose-h1:mb-4 prose-h1:mt-0
                                prose-h2:text-lg prose-h2:mb-3 prose-h2:mt-6
                                prose-h3:text-base prose-h3:mb-2 prose-h3:mt-4
                                prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-3
                                prose-li:text-gray-300 prose-li:mb-1
                                prose-strong:text-white
                                prose-ul:pl-5 prose-ol:pl-5
                                prose-code:text-purple-300 prose-code:bg-purple-500/10 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
                                prose-hr:border-white/10
                            ">
                                <ReactMarkdown>{previewing.content}</ReactMarkdown>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
